import { useCallback, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, FileSpreadsheet, Trash2, Upload } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  downloadImportTemplateCsv,
  downloadImportTemplateExcel,
  MEMBER_IMPORT_SHEET_COLUMNS,
  MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW,
  parseImportFile,
} from '@/lib/member-import'
import {
  useMemberImportConfirm,
  useMemberImportNotify,
  useMemberImportPreview,
} from '@/hooks/use-member-import'
import type { MemberImportPreviewRow, MemberImportRowInput } from '@riverpark/types'
import { DEFAULT_BOOTSTRAP_PASSWORD } from '@riverpark/types'
import { cn } from '@/lib/utils'

type Step = 'format' | 'upload' | 'preview' | 'confirm' | 'notify'

const STEPS: { id: Step; label: string }[] = [
  { id: 'format', label: 'Format' },
  { id: 'upload', label: 'Upload' },
  { id: 'preview', label: 'Review' },
  { id: 'confirm', label: 'Confirm' },
  { id: 'notify', label: 'Notify' },
]

function stepIndex(step: Step): number {
  return STEPS.findIndex((s) => s.id === step)
}

function summaryFromPreview(preview: MemberImportPreviewRow[]) {
  return {
    total: preview.length,
    valid: preview.filter((r) => r.status === 'valid').length,
    invalid: preview.filter((r) => r.status === 'invalid').length,
    warnings: preview.filter((r) => r.status === 'warning').length,
  }
}

function statusBadge(status: MemberImportPreviewRow['status']) {
  if (status === 'valid') return <Badge variant="paid">Valid</Badge>
  if (status === 'warning') return <Badge variant="pending">Warning</Badge>
  return <Badge variant="overdue">Invalid</Badge>
}

function ImportTempPasswordNotice({ className }: { className?: string }) {
  return (
    <div
      className={cn('rounded border-2 border-destructive/50 bg-destructive/5 px-4 py-3', className)}
      role="note"
    >
      <p className="font-sans text-sm font-medium text-destructive">
        Temporary password for every new member:{' '}
        <code className="rounded bg-card px-1.5 py-0.5 font-semibold tracking-wide text-destructive">
          {DEFAULT_BOOTSTRAP_PASSWORD}
        </code>
      </p>
      <p className="mt-1 font-sans text-xs text-destructive/90">
        Share this with members after import. They must change it when they first sign in.
      </p>
    </div>
  )
}

export function MemberImportWizard() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>('format')
  const [rows, setRows] = useState<MemberImportRowInput[]>([])
  const [previewRows, setPreviewRows] = useState<MemberImportPreviewRow[]>([])
  const [summary, setSummary] = useState({ total: 0, valid: 0, invalid: 0, warnings: 0 })
  const [fileError, setFileError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [importedCount, setImportedCount] = useState(0)
  const [importedMemberIds, setImportedMemberIds] = useState<string[]>([])
  const [notifyChoice, setNotifyChoice] = useState(true)

  const previewMutation = useMemberImportPreview()
  const importMutation = useMemberImportConfirm()
  const notifyMutation = useMemberImportNotify()

  const runPreview = useCallback(
    async (inputRows: MemberImportRowInput[]) => {
      setActionError(null)
      const { data } = await previewMutation.mutateAsync(inputRows)
      setPreviewRows(data.rows)
      setSummary(data.summary)
      return data
    },
    [previewMutation],
  )

  async function handleFileSelect(file: File) {
    setFileError(null)
    setActionError(null)
    try {
      const parsed = await parseImportFile(file)
      setRows(parsed)
      await runPreview(parsed)
      setStep('preview')
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Could not read the file')
    }
  }

  function updateRow(rowId: string, patch: Partial<MemberImportRowInput>) {
    setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)))
  }

  function removeRow(rowId: string) {
    setRows((current) => current.filter((row) => row.rowId !== rowId))
    setPreviewRows((current) => {
      const next = current.filter((row) => row.rowId !== rowId)
      setSummary(summaryFromPreview(next))
      return next
    })
    setActionError(null)
  }

  async function handleRevalidate() {
    try {
      await runPreview(rows)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Validation failed')
    }
  }

  async function handleImport() {
    setActionError(null)
    try {
      const latest = await runPreview(rows)
      if (latest.summary.invalid > 0) {
        setActionError('Fix all invalid rows before importing')
        setStep('preview')
        return
      }
      const { data } = await importMutation.mutateAsync({ rows, notify: false })
      setImportedCount(data.imported)
      setImportedMemberIds(data.memberIds)
      setStep('notify')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Import failed')
    }
  }

  async function handleNotifyFinish() {
    setActionError(null)
    if (notifyChoice && importedMemberIds.length > 0) {
      try {
        await notifyMutation.mutateAsync(importedMemberIds)
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Notification delivery failed')
        return
      }
    }
    navigate('/admin/members')
  }

  const currentStep = stepIndex(step)

  return (
    <PageShell>
      <PageHeader
        label="Management"
        title="Import members"
        description="Bulk import historical member records from a spreadsheet."
        action={
          <Link
            to="/admin/members"
            className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back to members
          </Link>
        }
      />

      <nav aria-label="Import progress" className="mb-8 flex flex-wrap gap-2">
        {STEPS.map((s, index) => (
          <div
            key={s.id}
            className={cn(
              'rounded border px-3 py-1.5 font-sans text-xs uppercase tracking-wide',
              index <= currentStep
                ? 'border-green/30 bg-green-pale text-status-paid-text'
                : 'border-border-2 bg-card text-muted-foreground',
            )}
          >
            {index + 1}. {s.label}
          </div>
        ))}
      </nav>

      {step === 'format' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Expected file format</h2>
          <p className="mt-2 max-w-2xl font-sans text-sm text-muted-foreground">
            Upload a spreadsheet with the columns below. <strong className="text-ink">Name</strong>{' '}
            and <strong className="text-ink">Telephone number</strong> are required on every row.
            <strong className="text-ink"> Plot No.</strong> is optional — leave it blank to create
            the member now and assign a plot later from Members or Plots. When you do include a
            plot, it must already exist in the plot register. Phone numbers may be entered as{' '}
            <code className="text-xs">2547XXXXXXXX</code> or{' '}
            <code className="text-xs">+2547XXXXXXXX</code>.
          </p>

          <ImportTempPasswordNotice className="mt-4 max-w-2xl" />

          <div className="mt-6 overflow-x-auto rounded border border-border-2">
            <table className="w-full min-w-[720px] border-collapse font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2/50">
                  {MEMBER_IMPORT_SHEET_COLUMNS.map((column) => (
                    <th key={column.header} className="px-4 py-2 text-left font-medium text-ink">
                      {column.header}
                      {column.required ? (
                        <span className="text-destructive" aria-hidden>
                          {' '}
                          *
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {MEMBER_IMPORT_TEMPLATE_EXAMPLE_ROW.map((value, index) => (
                    <td key={MEMBER_IMPORT_SHEET_COLUMNS[index]!.header} className="px-4 py-2">
                      {value || <span className="text-muted-foreground">—</span>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <ul className="mt-4 list-inside list-disc space-y-1 font-sans text-sm text-muted-foreground">
            <li>
              <strong className="text-ink">Name</strong> — full name (required)
            </li>
            <li>
              <strong className="text-ink">Telephone number</strong> — required;{' '}
              <code className="text-xs">254722576609</code> is fine
            </li>
            <li>
              <strong className="text-ink">Plot No.</strong> — optional; assigns ownership when set
              and the plot exists in the register. Leave blank to link a plot later in the admin
              portal.
            </li>
            <li>
              <strong className="text-ink">Status</strong> — member type: Resident, Developer, or
              Non resident (defaults to Resident if blank)
            </li>
            <li>
              <strong className="text-ink">Email</strong> — optional; if omitted, members sign in
              with phone only
            </li>
            <li>
              <strong className="text-ink">Borehole meter no.</strong>,{' '}
              <strong className="text-ink">KPLC meter no.</strong> — optional; updates the plot when{' '}
              <strong className="text-ink">Plot No.</strong> is set (blank cells do not clear
              existing values)
            </li>
          </ul>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="button" variant="secondary" onClick={downloadImportTemplateCsv}>
              <Download className="size-4" aria-hidden />
              Download CSV template
            </Button>
            <Button type="button" variant="secondary" onClick={downloadImportTemplateExcel}>
              <FileSpreadsheet className="size-4" aria-hidden />
              Download Excel template
            </Button>
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="button" onClick={() => setStep('upload')}>
              Continue to upload
            </Button>
          </div>
        </Card>
      )}

      {step === 'upload' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Upload your file</h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            Select a completed template or export from your records.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFileSelect(file)
            }}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-6 flex w-full flex-col items-center justify-center rounded border-2 border-dashed border-border-2 bg-cream-2/30 px-6 py-12 transition-colors hover:border-green/40 hover:bg-green-pale/20"
          >
            <Upload className="mb-3 size-8 text-muted-foreground" aria-hidden />
            <span className="font-sans text-sm font-medium text-ink">Choose CSV or Excel file</span>
            <span className="mt-1 font-sans text-xs text-muted-foreground">
              .csv, .xlsx up to 500 members
            </span>
          </button>

          {fileError && (
            <p className="mt-4 font-sans text-sm text-destructive" role="alert">
              {fileError}
            </p>
          )}

          {previewMutation.isPending && (
            <p className="mt-4 font-sans text-sm text-muted-foreground">Parsing and validating…</p>
          )}

          <div className="mt-8 flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep('format')}>
              Back
            </Button>
          </div>
        </Card>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <Card className="overflow-hidden p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-2 px-6 py-4">
              <div>
                <h2 className="font-serif text-xl text-ink">Review imported rows</h2>
                <p className="mt-1 font-sans text-sm text-muted-foreground">
                  {summary.total} rows — {summary.valid} valid, {summary.warnings} with warnings,{' '}
                  {summary.invalid} invalid. Remove rows you cannot fix before continuing.
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleRevalidate()}
                loading={previewMutation.isPending}
              >
                Re-validate
              </Button>
            </div>

            <div className="overflow-x-auto">
              {previewRows.length === 0 ? (
                <p className="px-6 py-8 font-sans text-sm text-muted-foreground">
                  No rows left to import. Upload a file again or go back to add members.
                </p>
              ) : (
                <table className="w-full min-w-[720px] border-collapse font-sans text-sm">
                  <thead>
                    <tr className="border-b border-border-2 bg-cream-2/50">
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Row</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Phone
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Plot
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Notes
                      </th>
                      <th className="w-12 px-4 py-3">
                        <span className="sr-only">Remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((preview) => {
                      const row = rows.find((r) => r.rowId === preview.rowId)

                      return (
                        <tr key={preview.rowId} className="border-b border-border-2 align-top">
                          <td className="px-4 py-3">{statusBadge(preview.status)}</td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.name ?? ''}
                              onChange={(e) => updateRow(preview.rowId, { name: e.target.value })}
                              aria-label="Name"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.phone ?? ''}
                              onChange={(e) => updateRow(preview.rowId, { phone: e.target.value })}
                              aria-label="Phone"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.email ?? ''}
                              onChange={(e) => updateRow(preview.rowId, { email: e.target.value })}
                              aria-label="Email"
                              placeholder="Optional"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              value={row?.plotNumber ?? ''}
                              onChange={(e) =>
                                updateRow(preview.rowId, { plotNumber: e.target.value })
                              }
                              aria-label="Plot number"
                              placeholder="Optional"
                            />
                            {preview.plotLabel && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {preview.plotLabel}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {preview.errors.map((msg) => (
                              <p key={msg} className="text-destructive">
                                {msg}
                              </p>
                            ))}
                            {preview.warnings.map((msg) => (
                              <p key={msg}>{msg}</p>
                            ))}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeRow(preview.rowId)}
                              aria-label={`Remove ${preview.name || 'row'}`}
                              title="Remove row"
                            >
                              <Trash2 className="size-4" aria-hidden />
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          {actionError && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}

          <div className="flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep('upload')}>
              Back
            </Button>
            <Button
              type="button"
              disabled={summary.invalid > 0 || summary.total === 0}
              onClick={() => {
                setActionError(null)
                setStep('confirm')
              }}
            >
              Continue to confirm
            </Button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Confirm import</h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            You are about to import{' '}
            <strong className="text-ink">{summary.valid + summary.warnings}</strong> members. Each
            will be created as an active member with a temporary password and must complete their
            profile on first login.
            {previewRows.some((r) => r.plotId) && (
              <>
                {' '}
                <strong className="text-ink">
                  {previewRows.filter((r) => r.plotId).length}
                </strong>{' '}
                {previewRows.filter((r) => r.plotId).length === 1 ? 'row has' : 'rows have'} a plot
                assignment that will be applied on import.
              </>
            )}
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded border border-border-2 bg-cream-2/30 px-4 py-3">
              <p className="font-sans text-label uppercase text-muted-foreground">Ready</p>
              <p className="font-serif text-2xl text-ink">{summary.valid + summary.warnings}</p>
            </div>
            <div className="rounded border border-border-2 bg-cream-2/30 px-4 py-3">
              <p className="font-sans text-label uppercase text-muted-foreground">Warnings</p>
              <p className="font-serif text-2xl text-ink">{summary.warnings}</p>
            </div>
            <div className="rounded border border-border-2 bg-cream-2/30 px-4 py-3">
              <p className="font-sans text-label uppercase text-muted-foreground">Invalid</p>
              <p className="font-serif text-2xl text-ink">{summary.invalid}</p>
            </div>
          </div>

          {actionError && (
            <p className="mt-4 font-sans text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}

          <div className="mt-8 flex justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep('preview')}>
              Back
            </Button>
            <Button
              type="button"
              loading={importMutation.isPending || previewMutation.isPending}
              onClick={() => void handleImport()}
            >
              Import members
            </Button>
          </div>
        </Card>
      )}

      {step === 'notify' && (
        <Card className="p-6">
          <h2 className="font-serif text-xl text-ink">Notify imported members?</h2>
          <p className="mt-2 font-sans text-sm text-muted-foreground">
            Successfully imported <strong className="text-ink">{importedCount}</strong> members. You
            can notify them with their sign-in details by SMS (and email when an address is on
            file).
          </p>

          <ImportTempPasswordNotice className="mt-4 max-w-xl" />

          <label className="mt-6 flex items-start gap-3 font-sans text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={notifyChoice}
              onChange={(e) => setNotifyChoice(e.target.checked)}
              className="mt-1"
            />
            <span>
              Send login credentials by SMS and email (when available)
              <span className="mt-1 block text-xs">
                SMS includes portal URL, sign-in email, and temporary password (160 chars max).
              </span>
            </span>
          </label>

          {actionError && (
            <p className="mt-4 font-sans text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}

          <div className="mt-8 flex justify-end">
            <Button
              type="button"
              loading={notifyMutation.isPending}
              onClick={() => void handleNotifyFinish()}
            >
              {notifyChoice ? 'Send notifications & finish' : 'Skip & finish'}
            </Button>
          </div>
        </Card>
      )}
    </PageShell>
  )
}
